import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import ExpertLayout from '../../components/ExpertLayout';
import PageHeader from '../../components/expert/PageHeader';
import { expertDashboardApi } from '../../lib/http';
import ServiceList from '../../components/expert/services/ServiceList';
import ServiceFormPanel from '../../components/expert/services/ServiceFormPanel';
import DeleteServiceDialog from '../../components/expert/services/DeleteServiceDialog';
import { ExpertService, ExpertProfile } from '../../types';

const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'USD ($)', symbol: '$' },
  { code: 'EUR', label: 'EUR (€)', symbol: '€' },
  { code: 'UAH', label: 'UAH (₴)', symbol: '₴' },
  { code: 'GBP', label: 'GBP (£)', symbol: '£' },
  { code: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
  { code: 'CAD', label: 'CAD (C$)', symbol: 'C$' },
  { code: 'CHF', label: 'CHF (Fr)', symbol: 'Fr' },
  { code: 'PLN', label: 'PLN (zł)', symbol: 'zł' },
];

const ExpertServices: React.FC = () => {
  const queryClient = useQueryClient();
  const [isFormPanelOpen, setIsFormPanelOpen] = useState(false);
  const [editingService, setEditingService] = useState<ExpertService | null>(null);
  const [deleteService, setDeleteService] = useState<ExpertService | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch expert profile for currency
  const { data: profileData } = useQuery({
    queryKey: ['expert-profile'],
    queryFn: async () => {
      const response = await expertDashboardApi.profile.get();
      return response.data as ExpertProfile;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Currency update mutation
  const currencyMutation = useMutation({
    mutationFn: async (currency: string) => {
      return expertDashboardApi.profile.update({ currency });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert-profile'] });
      queryClient.invalidateQueries({ queryKey: ['expert-services'] });
      toast.success('Currency updated');
    },
    onError: () => {
      toast.error('Failed to update currency');
    },
  });

  // Fetch services
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['expert-services'],
    queryFn: async () => {
      const response = await expertDashboardApi.services.list();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return expertDashboardApi.services.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert-services'] });
      toast.success('Service deleted successfully');
      setDeleteService(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete service');
    },
  });

  const handleAddService = () => {
    setEditingService(null);
    setIsFormPanelOpen(true);
  };

  const handleEditService = (service: ExpertService) => {
    setEditingService(service);
    setIsFormPanelOpen(true);
  };

  const handleDeleteService = (service: ExpertService) => {
    setDeleteService(service);
  };

  const handleFormSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['expert-services'] });
    setIsFormPanelOpen(false);
    setEditingService(null);
  };

  const handleConfirmDelete = () => {
    if (deleteService) {
      deleteMutation.mutate(deleteService.id);
    }
  };

  // Filter services based on search term
  const services = (data?.services || []).filter((service: ExpertService) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      service.name.toLowerCase().includes(term) ||
      (service.description && service.description.toLowerCase().includes(term))
    );
  });

  // Desktop: full button with text, Mobile: icon only
  const addServiceButton = (
    <>
      {/* Desktop button */}
      <button
        onClick={handleAddService}
        className="hidden sm:inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        New Service
      </button>
      {/* Mobile icon button */}
      <button
        onClick={handleAddService}
        className="sm:hidden flex items-center justify-center w-10 h-10 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all shadow-sm active:scale-95"
        aria-label="Add service"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </>
  );

  return (
    <ExpertLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <PageHeader
          title="Services"
          backTo="/app"
          action={addServiceButton}
        />

        {/* Currency Selector */}
        <div className="flex items-center gap-3 mb-6">
          <label className="text-sm font-medium text-gray-600">Currency:</label>
          <select
            value={profileData?.currency || 'USD'}
            onChange={(e) => currencyMutation.mutate(e.target.value)}
            disabled={currencyMutation.isPending}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
          {currencyMutation.isPending && (
            <svg className="animate-spin h-4 w-4 text-purple-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search services..."
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm transition-shadow"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-red-800">Error loading services</h3>
                <p className="mt-1 text-sm text-red-700">
                  {(error as any)?.message || 'Failed to load services. Please try again.'}
                </p>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['expert-services'] })}
                  className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Try again &rarr;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Services List */}
        {!isError && (
          <ServiceList
            services={services}
            isLoading={isLoading}
            onEdit={handleEditService}
            onDelete={handleDeleteService}
          />
        )}

        {/* Form Panel */}
        <ServiceFormPanel
          isOpen={isFormPanelOpen}
          onClose={() => {
            setIsFormPanelOpen(false);
            setEditingService(null);
          }}
          onSuccess={handleFormSuccess}
          editService={editingService}
          currency={profileData?.currency || 'USD'}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteServiceDialog
          isOpen={!!deleteService}
          service={deleteService}
          onClose={() => setDeleteService(null)}
          onConfirm={handleConfirmDelete}
          isDeleting={deleteMutation.isPending}
        />
      </div>
    </ExpertLayout>
  );
};

export default ExpertServices;
